import { CaseSubscriptionApi } from '@api/case-subscription/caseSubscription';
import { saveStripeInfoDuringSignup } from '@api/user/apiService';
import apiHandler from '../ApiHandler';
import { getDeviceToken, saveDeviceToken } from './trustedDevice';
import {
  LoginRequestData,
  LoginResponse,
  SignUpRequestData,
  SignUpResponse,
  addUserRequestData,
  UserResponse,
  UpdateUserRequest,
  PasswordChangeRequest,
  PasswordChangeResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyUserRequestData,
  IndividualSignUpRequestData,
  GenerateOTP,
  GenerateOTPResponse,
  VerifyOTP,
  VerifyOTPResponse,
  ResendOTP,
  ResendOTPResponse,
  RequestPhoneChangeResponse,
  ConfirmPhoneChangeResponse,
} from './model/types';

/**
 *
 * @returns Logged in user token
 * @param data
 * @param callback
 * @param errorCallback
 */
export const login = (
  data: LoginRequestData,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<LoginResponse>(`/user-auth/`, {
      ...data,
      device_token: getDeviceToken(data.email),
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const signUp = (
  data: SignUpRequestData,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<SignUpResponse>(`/sign-up/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const hasPhoneNumber = (
  email: string,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<{ has_phone_number: boolean }>(`/user-has-phone-number/`, { email })
    .then((response) => {
      console.log(response);
      callback(response as any);
    })
    .catch((error) => {
      console.log(error);
      errorCallback(error);
    });
};

export const addUserData = (
  accessToken: string,
  data: addUserRequestData,
  callback: (data: any) => void
) => {
  apiHandler
    .patch<LoginResponse>(`/sign-up/`, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback([]);
    });
};

export const sendActivationCode = (
  email: string,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post(`/email-verification/`, { email })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const getUserData = (
  accessToken: string,
  callback: (data: any) => void,
  errorCallback?: (data: any) => void
) => {
  apiHandler
    .get<UserResponse>(`/me/`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      if (errorCallback) {
        errorCallback(error);
      }
    });
};

export const updateUserDataRequest = (
  id: number,
  data: UpdateUserRequest,
  callback: (data: any) => void
) => {
  apiHandler
    .patch<UserResponse>(`/me/${id}/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(false); // or handle the error as you see fit
    });
};

export const updateUserImageRequest = (
  data: FormData,
  callback: (data: any) => void
) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  apiHandler
    .post<UserResponse>(`/me/update_picture/`, data, config)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(false); // or handle the error as you see fit
    });
};

export const passwordChangeRequest = (
  data: PasswordChangeRequest,
  callback: (data: any) => void,
  errorCallback: (response: any) => void
) => {
  apiHandler
    .put<PasswordChangeResponse>(`/password_change/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((response) => {
      errorCallback(response);
    });
};

export const forgotPasswordRequest = (
  data: ForgotPasswordRequest,
  callback: () => void,
  errorCallback: () => void,
  finalCallback: () => void
) => {
  apiHandler
    .post<ForgotPasswordResponse>(`/password_forgot/`, data)
    .then(() => {
      callback();
    })
    .catch(() => {
      errorCallback();
    })
    .finally(() => {
      finalCallback();
    });
};

// Resolves only when the password was changed. It used to call the same
// callback on failure, so a used or expired link looked like a success and
// sent the person to sign in with a password that was never set (ZLAYT-1477).
export const resetPasswordRequest = (data: ResetPasswordRequest) =>
  apiHandler.post<ResetPasswordResponse>(`/password_reset/`, data);

// WARNING: THE FUNCTION BELOW WAS INTRODUCED TO
// TEST SENTRY AND NEEDS TO BE REMOVED ASAP
export const testRequest = (callback: (data: any) => void) => {
  const data = {
    test: true,
  };
  apiHandler
    .post<UserResponse>(`/me/test/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(false); // or handle the error as you see fit
    });
};

export const verifyUser = (
  data: VerifyUserRequestData,
  callback: (data: any) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .patch<LoginResponse>(`/sign-up/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const individualSignUp = (
  data: IndividualSignUpRequestData,
  callback: (data: any) => void,
  errorCallback: (data: any) => void,
  payment_method_id?: string
) => {
  const requestData = payment_method_id ? { ...data, payment_method_id } : data;
  apiHandler
    .post<LoginResponse>(`/sign-up/`, requestData)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const businessSignUp = (
  data: IndividualSignUpRequestData,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<LoginResponse>(`/business-sign-up/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

/**
 * Whether an email can still be used to register. Backs the real-time check on the
 * signup field so a collision shows while typing instead of after submitting. The
 * endpoint is throttled, so callers should debounce. Resolves true on any failure:
 * a check that cannot run must never block a legitimate signup, and submitting will
 * still return a 409 if the address really is taken.
 */
export const checkEmailAvailability = (email: string): Promise<boolean> =>
  apiHandler
    .get<{ available: boolean }>(
      `/email-available/?email=${encodeURIComponent(email)}`
    )
    .then((response) => (response as any)?.available !== false)
    .catch(() => true);

export const resendVerificationCode = (
  email: string,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post(`/resend-verification-code/`, { email })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const generateOTP = (
  data: GenerateOTP,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<GenerateOTPResponse>(`/user/generate_otp/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const verifyOTP = (
  data: VerifyOTP,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<VerifyOTPResponse>(`/user/verify_otp/`, data)
    .then((response) => {
      saveDeviceToken(data.email, (response as any)?.device_token);
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const resendOTP = (
  data: ResendOTP,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<ResendOTPResponse>(`/user/resend_otp/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

// apiHandler's response interceptor already unwraps `data`, hence the casts.

/**
 * Send a verification code to a new phone number for the signed in user.
 * The number on the account does not change until confirmPhoneChange succeeds.
 */
export const requestPhoneChange = (phone: string) =>
  apiHandler.post<RequestPhoneChangeResponse>(`/user/phone-change/request/`, {
    phone,
  }) as unknown as Promise<RequestPhoneChangeResponse>;

/**
 * Confirm the code sent to the new number; on success the account's phone
 * number becomes that number.
 */
export const confirmPhoneChange = (otp_code: string) =>
  apiHandler.post<ConfirmPhoneChangeResponse>(`/user/phone-change/confirm/`, {
    otp_code,
  }) as unknown as Promise<ConfirmPhoneChangeResponse>;

/**
 * Register a user with payment information for a 5-day trial with Tier 1 plan
 * @param userData User registration data
 * @param paymentMethodId Stripe payment method ID
 * @param callback Success callback
 * @param errorCallback Error callback
 */
export const registerUserWithPayment = (
  userData: IndividualSignUpRequestData,
  paymentMethodId: string,
  callback: (data: any) => void,
  errorCallback: (data: any) => void,
  planPriceId?: number
) => {
  // First register the user
  individualSignUp(
    userData,
    (response) => {
      // Add a small delay to ensure the user is fully created in the database
      setTimeout(() => {
        // After successful registration, save the payment method
        saveStripeInfoDuringSignup(
          userData.email,
          paymentMethodId,
          async () => {
            // Both registration and payment method saved successfully
            console.log(
              'Payment method saved successfully for',
              userData.email
            );
            // Create Stripe trial subscription for default monthly plan and persist in our DB
            try {
              await CaseSubscriptionApi.createTrialSubscription(
                userData.email,
                planPriceId
              );
            } catch (e) {
              console.error(
                'Failed to create trial subscription at signup (individual):',
                e
              );
              // allow signup to proceed; user can retry from app
            }
            callback(response);
          },
          (error) => {
            // Payment method failed to save, but registration succeeded
            // We'll still consider this a success but log the error
            console.error('Error saving payment method:', error);
            callback(response);
          },
          () => {}
        );
      }, 1000); // 1 second delay
    },
    (error) => {
      errorCallback(error);
    }
  );
};

/**
 * Attach payment method and create subscription for an already-verified user
 * Used after email verification during signup flow
 * @param email User email (user already exists and is verified)
 * @param paymentMethodId Stripe payment method ID
 * @param callback Success callback
 * @param errorCallback Error callback
 * @param planPriceId Optional plan price ID (if null, creates trial)
 */
export const attachPaymentAndSubscribe = (
  email: string,
  paymentMethodId: string,
  callback: (data: any) => void,
  errorCallback: (data: any) => void,
  planPriceId?: number
) => {
  console.log(
    '[attachPaymentAndSubscribe] Starting for email:',
    email,
    'planPriceId:',
    planPriceId
  );

  // User already exists and is verified, just attach payment method
  saveStripeInfoDuringSignup(
    email,
    paymentMethodId,
    async () => {
      console.log(
        '[attachPaymentAndSubscribe] Payment method saved successfully for',
        email
      );

      // Create Stripe subscription ONLY when user explicitly selected a plan.
      // If planPriceId is undefined, user did not choose a plan/trial -> do not auto-subscribe.
      try {
        if (typeof planPriceId !== 'number') {
          callback({ success: true, skipped_subscription: true });
          return;
        }
        console.log(
          '[attachPaymentAndSubscribe] Creating subscription with planPriceId:',
          planPriceId
        );
        // planPriceId === -1 is a sentinel for explicit "Free Trial" selection.
        // In that case, omit planPriceId so backend selects the default monthly plan for trialing.
        const effectivePlanPriceId =
          planPriceId === -1 ? undefined : planPriceId;
        await CaseSubscriptionApi.createTrialSubscription(
          email,
          effectivePlanPriceId
        );
        console.log(
          '[attachPaymentAndSubscribe] Subscription created successfully'
        );
        callback({ success: true });
      } catch (e) {
        console.error(
          '[attachPaymentAndSubscribe] Failed to create subscription:',
          e
        );
        errorCallback(e);
      }
    },
    (error) => {
      console.error(
        '[attachPaymentAndSubscribe] Failed to save payment method:',
        error
      );
      errorCallback(error);
    },
    () => {}
  );
};
